/**
 * Portfolio Navigator - Stack Navigator for Portfolio screens
 * NOW THEME-AWARE
 */

import { createStackNavigator } from '@react-navigation/stack';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PortfolioScreen } from '../screens/portfolio/PortfolioScreen';
import { PositionDetailScreen } from '../screens/portfolio/PositionDetailScreen';
import { PositionFormScreen } from '../screens/portfolio/PositionFormScreen';
import { AddCashScreen } from '../screens/portfolio/AddCashScreen';
import { TradeFormScreen } from '../screens/portfolio/TradeFormScreen';
import { CorporateActionFormScreen } from '../screens/portfolio/CorporateActionFormScreen';
import { SimulateScreen } from '../screens/portfolio/SimulateScreen';
import { InstrumentDetail } from '../components/portfolio/InstrumentDetail';
import { DocumentReviewScreen } from '../screens/portfolio/DocumentReviewScreen';
import { WatchlistThesisScreen } from '../screens/watchlist/WatchlistThesisScreen';
import { useTheme } from '../contexts/ThemeContext';
import type { ExtractedDataResponse } from '../services/api/documentImportApi';
import { PositionHeader } from '../components/portfolio/PositionHeader';

export type PortfolioNavigatorParamList = {
  PortfolioHome: undefined;
  PositionDetail: { positionId: number };
  // T032 [US1]: Add PositionFormScreen for manual position entry
  // T041 [US2]: Support edit mode by accepting optional positionId
  AddPosition: { positionId?: number };
  AddCash: undefined;
  // T062 [US3]: Add TradeFormScreen for manual trade entry
  // Edit mode (tradeId) reserved for Phase 6
  AddTrade: { tradeId?: number; symbol?: string; currency?: string; instrumentName?: string };
  // Corporate Actions: Share splits, consolidations, bonus shares
  // Updated to use positionId instead of symbol (new API format)
  CorporateAction: { positionId?: number; symbol?: string; instrumentName?: string };
  // §8.1: standalone pre-trade impact simulation (also reachable from the + menu)
  Simulate: undefined;
  // T063: Add InstrumentDetail screen
  // Extended to support position_id, quantity, cost_price for duplicate resolution
  InstrumentDetail: {
    symbol: string;
    currency: string;
    instrument_id?: number;
    position_id?: number;
    quantity?: number;
    cost_price?: number;
  };
  // T095 [US5]: Add DocumentReviewScreen for AI extraction review
  // Extended to support merged data from multi-image import
  DocumentReview: {
    importId: number;
    extractedData?: ExtractedDataResponse;
    failedImages?: string[];
  };
  // T110: Add WatchlistThesisScreen for building thesis on watchlist items
  AddWatchlistThesis: {
    watchlistItemId: number;
    symbol: string;
    instrumentName?: string;
    currentPrice?: number | null;
  };
};

const Stack = createStackNavigator<PortfolioNavigatorParamList>();

export function PortfolioNavigator() {
  const { theme } = useTheme();
  const { t } = useTranslation();

  const headerStyles: any = useMemo(
    () => ({
      headerStyle: {
        backgroundColor: theme.colors.surface,
        borderBottomWidth: theme.id === 'bauhaus' ? 2 : 1,
        borderBottomColor: theme.colors.border,
        elevation: 0,
      },
      headerTitleStyle: {
        fontSize: theme.typography.fontSizeLg,
        fontWeight: theme.typography.fontWeightBold,
        color: theme.colors.text,
        textTransform: (theme.id === 'bauhaus' ? 'uppercase' : 'none') as 'uppercase' | 'none',
        letterSpacing: theme.id === 'bauhaus' ? -0.5 : 0,
      },
      headerTintColor: theme.colors.primary,
    }),
    [theme]
  );

  return (
    <Stack.Navigator screenOptions={{ headerShown: true, ...headerStyles }}>
      <Stack.Screen
        name="PortfolioHome"
        component={PortfolioScreen}
        options={{ headerShown: false }}
      />
      <Stack.Screen
        name="PositionDetail"
        component={PositionDetailScreen}
        options={({ route }) => ({
          headerTitle: () => <PositionHeader positionId={route.params.positionId} />,
        })}
      />
      <Stack.Screen
        name="AddPosition"
        component={PositionFormScreen}
        options={({ route }) => ({
          title: route.params?.positionId
            ? t('portfolio.editPosition')
            : t('portfolio.newPosition'),
        })}
      />
      <Stack.Screen
        name="AddCash"
        component={AddCashScreen}
        options={{ title: t('portfolio.addCash') }}
      />
      <Stack.Screen
        name="AddTrade"
        component={TradeFormScreen}
        options={({ route }) => ({
          title: route.params?.tradeId ? t('portfolio.editTrade') : t('portfolio.addTrade'),
        })}
      />
      <Stack.Screen
        name="CorporateAction"
        component={CorporateActionFormScreen}
        options={{ title: t('portfolio.corporateAction') }}
      />
      <Stack.Screen
        name="Simulate"
        component={SimulateScreen}
        options={{ title: t('simulate.title') }}
      />
      <Stack.Screen
        name="InstrumentDetail"
        component={InstrumentDetail}
        options={({ route }) => ({ title: route.params.symbol })}
      />
      <Stack.Screen
        name="DocumentReview"
        component={DocumentReviewScreen}
        options={{ title: t('portfolio.reviewExtractedData') }}
      />
      <Stack.Screen
        name="AddWatchlistThesis"
        component={WatchlistThesisScreen}
        options={({ route }) => ({
          title: t('watchlist.buildThesis', { defaultValue: 'Build a thesis' }),
        })}
      />
    </Stack.Navigator>
  );
}
